import React, { useEffect, useState } from "react";
import { Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * SLA / Turnaround-Time countdown badge.
 *
 * Used by the doctor dashboard case list + CaseDetail header. Shows:
 *   - "On time · 32h left"  (>20% of SLA remaining, green)
 *   - "Due soon · 5h left"  (<20% remaining, amber)
 *   - "OVERDUE · 3h late"   (past deadline, red)
 *   - "Completed in 24h"    (case done, slate)
 *
 * Props:
 *   createdAt: ISO string of case creation
 *   slaHours: 12 | 24 | 48 (defaults to 48)
 *   status: case status — completed cases freeze the badge
 *   completedAt: ISO string when status flipped to completed (optional)
 */
export default function TATBadge({ createdAt, slaHours = 48, status, completedAt, compact = false }) {
  const [now, setNow] = useState(() => Date.now());

  // Re-tick every 60s — cheap, only this component re-renders.
  useEffect(() => {
    if (status === "completed" || status === "rejected" || status === "refunded") return;
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, [status]);

  if (!createdAt) return null;
  const startMs = new Date(createdAt).getTime();
  const deadlineMs = startMs + slaHours * 3600 * 1000;
  const isDone = status === "completed";
  const isCancelled = status === "rejected" || status === "refunded";
  if (isCancelled) return null;

  const endRef = isDone ? new Date(completedAt || createdAt).getTime() : now;
  const elapsedH = Math.max(0, (endRef - startMs) / 3600 / 1000);
  const remainingH = (deadlineMs - now) / 3600 / 1000;

  // ----- Completed state: freeze the badge -----
  if (isDone) {
    const onTime = endRef <= deadlineMs;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
          onTime ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                 : "bg-amber-50 border-amber-200 text-amber-800"
        }`}
        data-testid="tat-badge"
        title={onTime ? "Delivered within SLA" : "Delivered after SLA"}
      >
        <CheckCircle2 className="w-3 h-3"/>
        {compact ? `${Math.round(elapsedH)}h` : `Completed in ${Math.round(elapsedH)}h`}
      </span>
    );
  }

  // ----- In-flight states -----
  const overdue = remainingH < 0;
  const dueSoon = !overdue && remainingH < slaHours * 0.2;
  const fmt = (h) => {
    const abs = Math.abs(h);
    if (abs >= 1) return `${Math.round(abs)}h`;
    return `${Math.max(1, Math.round(abs * 60))}m`;
  };

  if (overdue) {
    return (
      <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] font-bold uppercase tracking-wider bg-rose-50 border border-rose-200 text-rose-700 animate-pulse"
            data-testid="tat-badge"
            title={`Overdue by ${fmt(remainingH)}`}>
        <AlertTriangle className="w-3 h-3"/>
        {compact ? `+${fmt(remainingH)}` : `Overdue · +${fmt(remainingH)}`}
      </span>
    );
  }
  if (dueSoon) {
    return (
      <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-50 border border-amber-200 text-amber-800"
            data-testid="tat-badge"
            title={`${fmt(remainingH)} left before SLA breach`}>
        <Clock className="w-3 h-3"/>
        {compact ? fmt(remainingH) : `Due in ${fmt(remainingH)}`}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-50 border border-blue-200 text-blue-700"
          data-testid="tat-badge"
          title={`${fmt(remainingH)} of ${slaHours}h SLA remaining`}>
      <Clock className="w-3 h-3"/>
      {compact ? fmt(remainingH) : `${fmt(remainingH)} left`}
    </span>
  );
}
